export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:51823";

type RequestOptions = RequestInit;

export async function apiRequest<TResponse>(path: string, options: RequestOptions = {}): Promise<TResponse> {
  const url = new URL(path, API_BASE_URL);
  const headers = new Headers(options.headers ?? {});

  if (!headers.has("Content-Type") && options.body instanceof Blob === false && options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: options.credentials ?? "include",
  });

  if (!response.ok) {
    const message = await safeParseError(response);
    throw new Error(message ?? `Request failed with status ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as TResponse;
  }

  return (await response.text()) as unknown as TResponse;
}

async function safeParseError(response: Response): Promise<string | null> {
  try {
    const payload = await response.json();
    if (payload && typeof payload === "object" && "detail" in payload) {
      return formatErrorDetail((payload as any).detail);
    }
    return JSON.stringify(payload);
  } catch {
    return null;
  }
}

/**
 * Normalise FastAPI / Pydantic error `detail` payloads into a readable string.
 *
 * FastAPI returns three different shapes:
 *  - auth errors:           { "detail": "Insufficient permissions" }
 *  - manual HTTPException:  { "detail": "Title is required" } or an object
 *  - Pydantic validation:   { "detail": [{type, loc, msg, input}, ...] }
 *
 * Calling String() on the array previously produced "[object Object]".
 */
function formatErrorDetail(detail: unknown): string {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === "object") {
          const msg = (item as any).msg ?? (item as any).message;
          const loc = Array.isArray((item as any).loc)
            ? (item as any).loc.filter((p: unknown) => typeof p === "string").join(".")
            : null;
          if (msg && loc) return `${loc}: ${msg}`;
          if (msg) return String(msg);
          return JSON.stringify(item);
        }
        return String(item);
      })
      .join("; ");
  }
  if (typeof detail === "object") {
    const obj = detail as Record<string, unknown>;
    const msg = obj.message ?? obj.msg ?? obj.detail;
    if (msg) return String(msg);
    return JSON.stringify(detail);
  }
  return String(detail);
}
